  /* ==========================================================================
     モーダル (.js-modal--trigger / .js-modal--content)
     showBallon() などから直接開閉したいケースのために window.jsModal として
     open/close だけ公開する。それ以外のページ内コードとは独立している。
     ========================================================================== */
  (() => {
    const TRIGGER_SELECTOR = ".js-modal--trigger";
    const MODAL_SELECTOR = ".js-modal--content";
    const CLOSE_SELECTOR = "[data-modal-close]";
    const CLOSE_FALLBACK_MS = 400; // CSSのtransition-duration(0.25s)より余裕を持たせた保険

    const openModal = (modal) => {
      if (modal instanceof HTMLDialogElement && !modal.open) {
        modal.showModal();
        document.documentElement.classList.add("js-modal--open");
      }
    };

    const closeModal = (modal) => {
      if (modal instanceof HTMLDialogElement && modal.open) {
        modal.close();
      }
    };

    // 背面のスクロールロックは、フェードアウトのtransitionendを待ってから解除する。
    // transitionendはopacity以外(display/overlayなど)でも発火するためプロパティ名を
    // 判定し、さらに何らかの理由でopacityのtransitionendが発火しないケース
    // (開いた直後に即座に閉じた等、値が実質変化しない場合)に備えてタイムアウトでも
    // フォールバックする。
    const handleModalClose = (modal) => {
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (prefersReducedMotion) {
        document.documentElement.classList.remove("js-modal--open");
        return;
      }

      const finish = () => {
        document.documentElement.classList.remove("js-modal--open");
        modal.removeEventListener("transitionend", handleTransitionEnd);
        clearTimeout(fallbackTimer);
      };

      const handleTransitionEnd = (event) => {
        if (event.propertyName === "opacity") finish();
      };

      modal.addEventListener("transitionend", handleTransitionEnd);
      const fallbackTimer = setTimeout(finish, CLOSE_FALLBACK_MS);
    };

    // dialogのcloseイベントはバブリングしないため委譲では拾えない。
    // 各dialogに直接リスナーを張る必要があるので、新しく追加されたdialogも
    // MutationObserverで検知してここを通す。あわせて、祖先にdisplay:noneが
    // あっても表示できるよう(トップレイヤーはdisplay:none祖先の影響を受けて
    // 描画されなくなる仕様があるため) bodyの直下へ退避させておく。
    const boundModals = new WeakSet();
    const bindModal = (modal) => {
      if (boundModals.has(modal)) return;
      boundModals.add(modal);

      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }

      modal.addEventListener("close", () => handleModalClose(modal));
    };

    // span等のネイティブに操作できない要素にキーボード操作を持たせる。
    // button/aはネイティブ挙動に任せるため対象外。
    const enhancedTriggers = new WeakSet();
    const enhanceTrigger = (trigger) => {
      if (enhancedTriggers.has(trigger)) return;
      enhancedTriggers.add(trigger);

      if (trigger.tagName === "BUTTON" || trigger.tagName === "A") return;
      if (!trigger.hasAttribute("role")) trigger.setAttribute("role", "button");
      if (!trigger.hasAttribute("tabindex")) trigger.setAttribute("tabindex", "0");
    };

    const scan = (root) => {
      root.querySelectorAll(MODAL_SELECTOR).forEach(bindModal);
      root.querySelectorAll(TRIGGER_SELECTOR).forEach(enhanceTrigger);

      if (root instanceof Element) {
        if (root.matches(MODAL_SELECTOR)) bindModal(root);
        if (root.matches(TRIGGER_SELECTOR)) enhanceTrigger(root);
      }
    };

    document.addEventListener("DOMContentLoaded", () => scan(document));

    // 後から挿入された.js-modal--trigger / .js-modal--contentにも対応する
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });

    // クリックはすべて委譲で処理するため、後から追加された要素にも自動で効く
    document.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(TRIGGER_SELECTOR);
      if (trigger) {
        const targetId = trigger.dataset.modalTarget;
        const modal = targetId && document.getElementById(targetId);
        openModal(modal);
        return;
      }

      const closeButton = event.target.closest(CLOSE_SELECTOR);
      if (closeButton) {
        closeModal(closeButton.closest(MODAL_SELECTOR));
        return;
      }

      // dialog自身(背景/backdrop部分)がクリックされた場合のみ閉じる
      if (event.target.matches(MODAL_SELECTOR)) {
        closeModal(event.target);
      }
    });

    // Enter / Spaceでトリガーを操作できるようにする(button, aは対象外)
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(TRIGGER_SELECTOR);
      if (!trigger || trigger.tagName === "BUTTON" || trigger.tagName === "A") return;

      event.preventDefault();
      const targetId = trigger.dataset.modalTarget;
      const modal = targetId && document.getElementById(targetId);
      openModal(modal);
    });

    // showBallon() など、トリガー要素を介さずJSから直接開閉したい場合に使う
    window.jsModal = { open: openModal, close: closeModal };
  })();

  /* ==========================================================================
     タブ (.js-tab--trigger / .js-tab--content)
     モーダルとは完全に独立したIIFE。変数もセレクタも共有していない。
     ========================================================================== */
  (() => {
    const TRIGGER_SELECTOR = ".js-tab--trigger";
    const CONTENT_SELECTOR = ".js-tab--content";
    const CONTENT_GROUP_SELECTOR = ".js-tab--content-group";
    const ACTIVE_CLASS = "is-active";

    // .js-tab--content-groupの上端(ドキュメント座標)から、#js-fixed--menuの高さ分だけ
    // 上の位置までスクロールする(そうしないと固定表示された#js-fixed--menuに
    // .js-tab--content-groupの上端が隠れてしまうため)。
    // html { scroll-behavior: smooth } により自動的に滑らかにスクロールする。
    const scrollToContentGroupTop = () => {
      const contentGroup = document.querySelector(CONTENT_GROUP_SELECTOR);
      if (!contentGroup) return;

      const fixedMenuHeight = window.jsFixedMenu ? window.jsFixedMenu.getHeight() : 0;
      const top = contentGroup.getBoundingClientRect().top + window.scrollY - fixedMenuHeight;
      window.scrollTo({ top, left: 0 });
    };

    // scrollがtrueのときだけ.js-tab--content-groupの上端までスクロールする
    // (初期表示時のデフォルトタブ活性化ではスクロールさせたくないため呼び分ける)
    const activateTab = (trigger, { scroll = false } = {}) => {
      const targetId = trigger.dataset.tabTarget;
      const target = targetId && document.getElementById(targetId);
      if (!(target instanceof HTMLElement) || !target.matches(CONTENT_SELECTOR)) return;

      document.querySelectorAll(TRIGGER_SELECTOR).forEach((t) => {
        const isActive = t === trigger;
        t.classList.toggle(ACTIVE_CLASS, isActive);
        t.setAttribute("aria-selected", String(isActive));
      });

      document.querySelectorAll(CONTENT_SELECTOR).forEach((content) => {
        content.classList.toggle(ACTIVE_CLASS, content === target);
      });

      if (scroll) scrollToContentGroupTop();
    };

    // span等のネイティブに操作できない要素にキーボード操作を持たせる。
    const enhancedTriggers = new WeakSet();
    const enhanceTrigger = (trigger) => {
      if (enhancedTriggers.has(trigger)) return;
      enhancedTriggers.add(trigger);

      if (!trigger.hasAttribute("role")) trigger.setAttribute("role", "tab");
      if (trigger.tagName === "BUTTON" || trigger.tagName === "A") return;
      if (!trigger.hasAttribute("tabindex")) trigger.setAttribute("tabindex", "0");
    };

    const scan = (root) => {
      root.querySelectorAll(TRIGGER_SELECTOR).forEach(enhanceTrigger);
      if (root instanceof Element && root.matches(TRIGGER_SELECTOR)) enhanceTrigger(root);
    };

    // 初期表示時、どのタブもアクティブでなければ先頭のトリガーを開いておく
    const activateDefaultTab = () => {
      if (document.querySelector(`${CONTENT_SELECTOR}.${ACTIVE_CLASS}`)) return;
      const firstTrigger = document.querySelector(TRIGGER_SELECTOR);
      if (firstTrigger) activateTab(firstTrigger);
    };

    document.addEventListener("DOMContentLoaded", () => {
      scan(document);
      activateDefaultTab();
    });

    // トリガー/コンテンツが後から動的に追加されるケースにも対応する
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest(TRIGGER_SELECTOR);
      if (trigger) activateTab(trigger, { scroll: true });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(TRIGGER_SELECTOR);
      if (!trigger || trigger.tagName === "BUTTON" || trigger.tagName === "A") return;

      event.preventDefault();
      activateTab(trigger, { scroll: true });
    });
  })();

  const OPTION_VALUES = ["選択してください", "カレー", "アイスクリーム", "カップ"];

  // js-select--vidを持つjs-select-widgetを初期化する(該当しなければfalseを返す)。
  // - js-select--vidで選ばれたoptionのvalue(vid)に対応するdata-stockの数だけ、
  //   js-select--quantityの選択肢(0〜在庫数)を作り直す
  // - js-select--vidが未選択の間はjs-select--quantityをdisabledにする
  // - vid選択中は.js-select--quantity--wrapの直前に在庫数(.js-stock)を表示する
  function initVidSelectWidget(widget) {
    const vidSelect = widget.querySelector(".js-select--vid");
    if (!vidSelect) return false;

    const quantitySelect = widget.querySelector(".js-select--quantity");
    const quantityWrap = widget.querySelector(".js-select--quantity--wrap");
    const stockInputs = Array.from(widget.querySelectorAll("input[type='hidden'][data-vid]"));

    const getStock = (vid) => {
      const stockInput = stockInputs.find((input) => input.dataset.vid === vid);
      return stockInput ? parseInt(stockInput.dataset.stock, 10) || 0 : 0;
    };

    function syncQuantityOptions() {
      const vid = vidSelect.value !== "0" ? vidSelect.value : "";
      const stock = vid ? getStock(vid) : 0;

      // 残り在庫表示(.js-stock--wrap)を.js-select--quantity--wrapの直前に出し直す
      const existingStockWrap = widget.querySelector(".js-stock--wrap");
      if (existingStockWrap) existingStockWrap.remove();

      if (vid && quantityWrap) {
        const stockWrap = document.createElement("div");
        stockWrap.className = "js-stock--wrap c-stock_wrap";
        stockWrap.innerHTML = `<p>残り<span class="js-stock">${stock}</span>席</p>`;
        quantityWrap.prepend(stockWrap);
      }

      if (!quantitySelect) return;

      quantitySelect.innerHTML = "";

      const placeholder = document.createElement("option");
      placeholder.value = "0";
      placeholder.textContent = "参加人数をご選択ください";
      quantitySelect.appendChild(placeholder);

      for (let i = 1; i <= stock; i++) {
        const option = document.createElement("option");
        option.value = String(i);
        option.textContent = String(i);
        quantitySelect.appendChild(option);
      }

      // vid未選択の間はquantity-select自体を操作できないようにしておく
      quantitySelect.disabled = !vid;
      quantitySelect.value = "0";
    }

    vidSelect.addEventListener("change", syncQuantityOptions);
    syncQuantityOptions(); // 初期状態(未選択)の選択肢に揃えておく

    // input[type=reset]によるフォームリセットはvid-selectの値を戻すだけで
    // changeイベントを発火しないため、reset後に1ティック遅らせて同期する
    const form = widget.closest("form");
    if (form) {
      form.addEventListener("reset", () => {
        setTimeout(syncQuantityOptions, 0);
      });
    }

    return true;
  }

  // js-count-selectを持つjs-select-widgetを初期化する。
  // - data-mode="dynamic"のときだけ、個数選択に応じて.c-dynamic-selectを生成する
  // - ラジオを持つ場合は、選択中のラジオのdata-stockに合わせてjs-count-selectの
  //   選択肢(0〜在庫数)を作り直す
  function initSelectWidget(widget) {
    if (initVidSelectWidget(widget)) return;

    const countSelect = widget.querySelector(".js-count-select");
    if (!countSelect) return;

    const selectContainer = widget.querySelector(".js-select-container");
    const isDynamic = countSelect.dataset.mode === "dynamic";
    const radios = Array.from(widget.querySelectorAll("input[type='radio']"));

    // data-stockが0(在庫なし)のラジオは選択できないようにし、
    // 親の.c-input_wrapにもis-disabledを付与しておく(スタイル側で判別できるように)
    radios.forEach((radio) => {
      const stock = parseInt(radio.dataset.stock, 10) || 0;
      if (stock > 0) return;

      radio.disabled = true;
      const inputWrap = radio.closest(".c-input_wrap");
      if (inputWrap) inputWrap.classList.add("is-disabled");
    });

    // count-selectの現在の値に合わせて.c-dynamicSelect-wrapの数を過不足なく揃える(dynamicモードのみ)
    function syncDynamicSelects() {
      if (!isDynamic || !selectContainer) return;

      const count = parseInt(countSelect.value, 10) || 0;
      const existingWraps = Array.from(selectContainer.querySelectorAll(".c-dynamicSelect-wrap"));

      if (count < existingWraps.length) {
        // 減らす場合: 末尾から余分な.c-dynamicSelect-wrapを削除するだけ(残すselectの値はそのまま)
        for (let i = existingWraps.length - 1; i >= count; i--) {
          existingWraps[i].remove();
        }
      } else if (count > existingWraps.length) {
        // 増やす場合: 既存selectはそのまま、不足分だけ新規追加(初期値は「選択する」)
        for (let i = existingWraps.length; i < count; i++) {
          const wrap = document.createElement("div");
          wrap.className = "c-dynamicSelect-wrap";

          const label = document.createElement("label");
          label.textContent = `${i + 1}品目`;

          const select = document.createElement("select");
          select.className = "c-dynamic-select";
          select.dataset.index = i;

          OPTION_VALUES.forEach(optionText => {
            const option = document.createElement("option");
            option.value = optionText;
            option.textContent = optionText;
            select.appendChild(option);
          });

          wrap.appendChild(label);
          wrap.appendChild(select);
          selectContainer.appendChild(wrap);
        }
      }
    }

    // 個数選択が変わったら、その数だけselectを用意する(既存の選択値は保持)
    countSelect.addEventListener("change", syncDynamicSelects);

    // ラジオを持つウィジェットのみ: 選択中のラジオのdata-stockに合わせて
    // count-selectの選択肢を0〜在庫数で作り直す。ラジオを切り替えると
    // 在庫数が変わり、それまで選んでいた個数が在庫超過になりうるため、
    // count-selectの値と生成済みの動的selectをあわせてリセットする。
    function syncOptionsWithStock() {
      if (radios.length === 0) return;

      const checkedRadio = widget.querySelector("input[type='radio']:checked");
      const stock = checkedRadio ? parseInt(checkedRadio.dataset.stock, 10) || 0 : 0;

      countSelect.innerHTML = "";

      const placeholder = document.createElement("option");
      placeholder.value = "0";
      placeholder.textContent = "0";
      countSelect.appendChild(placeholder);

      for (let i = 1; i <= stock; i++) {
        const option = document.createElement("option");
        option.value = String(i);
        option.textContent = String(i);
        countSelect.appendChild(option);
      }

      // ラジオ未選択の間はcount-select自体を操作できないようにしておく
      countSelect.disabled = !checkedRadio;
      countSelect.value = "0";

      // ラジオが選択されるまでは、.c-select-wrapの直前に案内メッセージを
      // 表示する(選択されたら消す)。js-select-containerの有無に関わらず、
      // ラジオを持つウィジェットなら共通で動く(event01/event02どちらも対象)。
      // 注意: .c-select-wrapは「生成するselectの数」ブロック全体を囲む
      // 本来のマークアップで既に使われているため、メッセージ側はそれとは
      // 別名(js-select-container-message)を使う(同名だと誤って本体UIを
      // 削除してしまう)。
      if (checkedRadio) {
        const existingMessage = widget.querySelector(".js-select-container-message");
        if (existingMessage) existingMessage.remove();
      } else if (!widget.querySelector(".js-select-container-message")) {
        const selectWrapBlock = widget.querySelector(".c-select-wrap");

        if (selectWrapBlock) {
          const message = document.createElement("div");
          message.className = "js-select-container-message";
          message.innerHTML = "<p>開催時間を選択してください。</p>";

          selectWrapBlock.before(message);
        }
      }

      syncDynamicSelects();
    }

    radios.forEach((radio) => radio.addEventListener("change", syncOptionsWithStock));
    syncOptionsWithStock(); // 初期状態(未選択)の選択肢に揃えておく

    // input[type=reset]によるフォームリセットはラジオ・count-selectの値を戻す
    // だけでchangeイベントを発火しないため、動的に増やしたselectや
    // 在庫に応じたcount-selectの選択肢がそのまま残ってしまう。
    // resetイベントは実際に値が戻る"前"に発火する仕様なので、
    // 値が戻った後の状態で同期できるよう1ティック遅らせる。
    const form = widget.closest("form");
    if (form) {
      form.addEventListener("reset", () => {
        setTimeout(() => {
          syncOptionsWithStock(); // ラジオがなければ何もしない
          syncDynamicSelects();
        }, 0);
      });
    }
  }

  // 1つのjs-widget-group(複数のjs-select-widgetと、それらをまとめて送信する共有js-submit)単位で初期化する
  function initWidgetGroup(group) {
    const widgets = Array.from(group.querySelectorAll(".js-select-widget"));
    const buttonWrap = group.querySelector(".js-buttonWrap");
    const submitButton = buttonWrap.querySelector(".js-submit");

    widgets.forEach(initSelectWidget);

    // このウィジェットのvidを取得する。
    // - ラジオボタンが存在する場合: チェック中のラジオのvalue(未選択ならnull)
    // - ラジオボタンが存在しない場合: js-count-selectのdata-variant-id(未指定ならnull)
    function getVid(widget) {
      const vidSelect = widget.querySelector(".js-select--vid");
      if (vidSelect) {
        return vidSelect.value !== "0" ? vidSelect.value : null;
      }

      const radios = widget.querySelectorAll("input[type='radio']");
      if (radios.length > 0) {
        const checkedRadio = widget.querySelector("input[type='radio']:checked");
        return checkedRadio ? checkedRadio.value : null;
      }

      const countSelect = widget.querySelector(".js-count-select");
      return (countSelect && countSelect.dataset.variantId) || null;
    }

    // 1つのjs-select-widgetを配列に変換する。
    // - js-select--vidを持つ場合: { vid, quantity } の1件(js-select--quantityの選択値がquantity)
    // - data-mode="dynamic": { vid, quantity, model } で、生成済みの.c-dynamic-select 1つにつき1件(quantityは固定で1)
    // - data-mode="static"(またはmode未指定): { vid, quantity } の1件(js-count-selectで選んだ値がquantity)
    function getWidgetValues(widget) {
      const vid = getVid(widget);
      if (!vid) return [];

      const vidSelect = widget.querySelector(".js-select--vid");
      if (vidSelect) {
        const quantitySelect = widget.querySelector(".js-select--quantity");
        if (!quantitySelect) return [];
        return [{ vid, quantity: quantitySelect.value }];
      }

      const countSelect = widget.querySelector(".js-count-select");
      if (!countSelect) return [];

      if (countSelect.dataset.mode === "dynamic") {
        // .c-dynamic-select 1つにつき1商品として扱うため、quantityは固定で1
        return Array.from(widget.querySelectorAll(".c-dynamic-select"))
          .map((sel) => ({ vid, quantity: "1", model: sel.value }));
      }

      return [{ vid, quantity: countSelect.value }];
    }

    // 1つのjs-select-widgetが送信可能な状態かどうかを判定する
    // (vidが取得できない、js-select--quantity/js-count-selectが未選択のまま、
    //  data-mode="dynamic"で動的selectに「選択する」が残っている場合はfalse)
    function isWidgetComplete(widget) {
      const vid = getVid(widget);
      if (!vid) return false;

      const vidSelect = widget.querySelector(".js-select--vid");
      if (vidSelect) {
        const quantitySelect = widget.querySelector(".js-select--quantity");
        return !!quantitySelect && quantitySelect.value !== "0";
      }

      const countSelect = widget.querySelector(".js-count-select");
      if (!countSelect || countSelect.value === "0") return false;

      if (countSelect.dataset.mode === "dynamic") {
        const dynamicSelects = Array.from(widget.querySelectorAll(".c-dynamic-select"));
        return dynamicSelects.length > 0 && dynamicSelects.every(sel => sel.value !== "選択してください");
      }

      return true;
    }

    // グループ内の、送信可能な状態のjs-select-widgetの選択値だけをまとめて取得する
    // (未完了のウィジェットは対象から除外する)
    function getSelectedValues() {
      return widgets.filter(isWidgetComplete).flatMap(getWidgetValues);
    }

    // グループ内のいずれか1つのjs-select-widgetが送信可能な状態になればbuttonを有効化
    function updateButtonState() {
      submitButton.disabled = !widgets.some(isWidgetComplete);
    }

    // buttonのonclick属性に、グループ内全js-select-widgetの選択値配列を直接埋め込む
    function updateButtonOnclick() {
      submitButton.setAttribute("onclick", `buttonClick(${JSON.stringify(getSelectedValues())})`);
    }

    // グループ内のラジオ・js-count-select・動的selectいずれかが変わるたびに判定し直す
    // (change イベントはbubbleするため、js-count-selectの.c-dynamic-select生成処理が先に走った後に実行される)
    group.addEventListener("change", () => {
      updateButtonState();
      updateButtonOnclick();
    });

    // input[type=reset]によるフォームリセットはchangeイベントを発火しないため、
    // 上のchangeリスナーだけではbuttonの状態・onclickが古いまま残ってしまう。
    // resetイベントは値が戻る"前"に発火する仕様なので、1ティック遅らせてから
    // (widget側のselect数同期と同じタイミングで)再計算する。
    group.addEventListener("reset", () => {
      setTimeout(() => {
        updateButtonState();
        updateButtonOnclick();
      }, 0);
    });

    // disabledでないjs-submitが押されたら、buttonWrapにis-activeを付与し、3秒後に解除する
    submitButton.addEventListener("click", () => {
      if (submitButton.disabled) return;

      buttonWrap.classList.add("is-active");
      setTimeout(() => {
        buttonWrap.classList.remove("is-active");
      }, 3000);

      showBallon();
    });

    // 初期状態(まだ何も選択されていない)ではbuttonを無効化
    updateButtonState();
    updateButtonOnclick();
  }

  // ページ内の全てのjs-widget-groupをそれぞれ独立して初期化する
  document.querySelectorAll(".js-widget-group").forEach(initWidgetGroup);

  // showBallon()が使うモーダル本体と、動的コンテンツを差し込む場所
  const ballonModal = document.getElementById("js-modal--content-ballon");
  const ballonBody = ballonModal.querySelector(".js-modal--ballon-body");

  // モーダルを閉じたら、createElementで生成したballonをDOMから削除する
  // (閉じるボタン/背景クリック/Escキーいずれの閉じ方でも、dialogのcloseイベントは必ず発火する)
  ballonModal.addEventListener("close", () => {
    ballonBody.innerHTML = "";
  });

  // カート追加の通知(ballon)をモーダルとして表示する。
  // 中身は毎回createElementで新しく生成し、モーダルが閉じたタイミングで削除される。
  function showBallon() {
    ballonBody.innerHTML = "";

    const ballon = document.createElement("div");
    ballon.className = "c-ballon";
    ballon.innerHTML = `
      <div class="c-ballon_inner">
        <p class="c-ballon_thanks">カートに追加しました</p>
        <p class="c-button01"><a href="/cart">購入手続きに進む</a></p>
        <p class="c-ballon_note">修正や削除はカートページで行えます</p>
      </div>
    `;

    ballonBody.appendChild(ballon);
    window.jsModal.open(ballonModal);
  }

  // button押下時に呼ばれる。onclick属性には各ウィジェットの選択値配列が直接埋め込まれている
  //function buttonClick(selectedValues) {
  //  test(...selectedValues);
  //}

  // 実際の処理はここに実装する
  function test(...params) {
    console.log("test関数に渡されたパラメータ:", params);
    alert("test(" + params.map(p => JSON.stringify(p)).join(", ") + ") が呼ばれました");
  }

  /* ==========================================================================
     スライドショー (.js-slideNN / .js-slideNN-item)
     ページ内の他の機能とは独立したIIFE。
     ========================================================================== */
  (() => {
    // 切り替え間隔(ミリ秒)。変更したいときはここだけ書き換えればよい。
    const SLIDE_INTERVAL_MS = 4000;

    const ACTIVE_CLASS = "is-active";
    const CONTAINER_CLASS_PATTERN = /^js-slide\d+$/;

    // .js-slideNN 1つを初期化し、.js-slideNN-item を順番にis-active化するタイマーを開始する
    function initSlide(container, slideClassName) {
      const items = Array.from(container.querySelectorAll(`.${slideClassName}-item`));
      if (items.length === 0) return;

      let currentIndex = items.findIndex((item) => item.classList.contains(ACTIVE_CLASS));
      if (currentIndex === -1) {
        currentIndex = 0;
        items[currentIndex].classList.add(ACTIVE_CLASS);
      }

      setInterval(() => {
        items[currentIndex].classList.remove(ACTIVE_CLASS);
        currentIndex = (currentIndex + 1) % items.length; // 最後まで行ったら先頭に戻る
        items[currentIndex].classList.add(ACTIVE_CLASS);
      }, SLIDE_INTERVAL_MS);
    }

    document.querySelectorAll('[class*="js-slide"]').forEach((container) => {
      const slideClassName = Array.from(container.classList).find((cls) => CONTAINER_CLASS_PATTERN.test(cls));
      if (slideClassName) initSlide(container, slideClassName);
    });
  })();

  /* ==========================================================================
     スクロールでのis-active付与 (.js-addClass01 / .js-addClass02)
     ページ内の他の機能とは独立したIIFE。
     ========================================================================== */
  (() => {
    const TARGET_SELECTOR = ".js-addClass01";
    const NEXT_CLASS = "js-addClass02";
    const ACTIVE_CLASS = "is-active";
    const NEXT_DELAY_MS = 1200; // js-addClass01の直後の兄弟js-addClass02にis-activeを付けるまでの遅延
    const CHILD_DELAY_MS = 2000; // js-addClass01の子孫js-addClass02にis-activeを付けるまでの遅延

    const targets = Array.from(document.querySelectorAll(TARGET_SELECTOR));
    if (targets.length === 0) return;

    // js-addClass01にis-activeを付与し、
    // - 直後の兄弟がjs-addClass02なら1.2秒遅らせてそちらにもis-activeを付与する
    // - 要素内(子孫)にjs-addClass02があれば0.5秒遅らせてそちらにもis-activeを付与する
    const activate = (el) => {
      el.classList.add(ACTIVE_CLASS);

      const next = el.nextElementSibling;
      if (next && next.classList.contains(NEXT_CLASS)) {
        setTimeout(() => next.classList.add(ACTIVE_CLASS), NEXT_DELAY_MS);
      }

      const children = el.querySelectorAll(`.${NEXT_CLASS}`);
      children.forEach((child) => {
        setTimeout(() => child.classList.add(ACTIVE_CLASS), CHILD_DELAY_MS);
      });
    };

    // 各js-addClass01の上端がウィンドウの高さの1/3のラインまで
    // スクロールしてきたらis-activeを付与する(一度付いたら戻さない)
    const checkTargets = () => {
      const triggerLine = window.innerHeight / 4 * 3;

      targets.forEach((el) => {
        if (el.classList.contains(ACTIVE_CLASS)) return;
        if (el.getBoundingClientRect().top <= triggerLine) activate(el);
      });
    };

    window.addEventListener("scroll", checkTargets, { passive: true });
    window.addEventListener("resize", checkTargets);
    checkTargets(); // 初期表示時点で既に条件を満たしている要素があれば即時反映する
  })();

  /* ==========================================================================
     スクロールでの動画再生 (.js-video)
     ページ内の他の機能とは独立したIIFE。
     ========================================================================== */
  (() => {
    const TARGET_SELECTOR = ".js-video";

    const videos = Array.from(document.querySelectorAll(TARGET_SELECTOR));
    if (videos.length === 0) return;

    const played = new WeakSet();

    // 各videoの上端がウィンドウの高さの2/3のラインまでスクロールしてきたら再生する
    // (一度再生したら再チェックしない)
    const checkVideos = () => {
      const triggerLine = (window.innerHeight * 2) / 3;

      videos.forEach((video) => {
        if (played.has(video)) return;
        if (video.getBoundingClientRect().top > triggerLine) return;

        played.add(video);
        // ブラウザの自動再生ポリシーで再生が拒否されても(通常はmutedなら許可される)、
        // 未処理のPromise拒否として警告が出ないようにしておく
        video.play().catch(() => {});
      });
    };

    window.addEventListener("scroll", checkVideos, { passive: true });
    window.addEventListener("resize", checkVideos);
    checkVideos(); // 初期表示時点で既に条件を満たしているvideoがあれば即時反映する
  })();

  /* ==========================================================================
     ページ内リンクのスムーズスクロール (href="#..." のaタグ)
     ページ内の他の機能とは独立したIIFE。
     ========================================================================== */
  (() => {
    const DURATION_MS = 600; // スクロールアニメーションの時間(ミリ秒)。変更したいときはここだけ書き換えればよい

    const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

    // html { scroll-behavior: smooth } が効いていると、この関数が毎フレーム呼ぶ
    // scrollTo自体もブラウザ側でさらに滑らかに動かそうとして二重にアニメーションが
    // かかり、カクついたり意図した速さにならなくなる。behavior: "auto" を明示して
    // 毎フレーム即座にその位置へジャンプさせることで、滑らかさはこのrAFループの
    // イージングだけが担うようにし、他の(CSSも含めた)スクロール挙動と干渉しない
    // ようにしている。
    const animateScrollTo = (targetY) => {
      const startY = window.scrollY;
      const distance = targetY - startY;
      if (distance === 0) return;

      const startTime = performance.now();

      const step = (now) => {
        const progress = Math.min((now - startTime) / DURATION_MS, 1);
        window.scrollTo({ top: startY + distance * easeInOutQuad(progress), left: 0, behavior: "auto" });

        if (progress < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    };

    // クリックはすべて委譲で処理するため、後から追加されたページ内リンクにも自動で効く
    document.addEventListener("click", (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a[href^="#"]');
      if (!link) return;

      const hash = link.getAttribute("href");
      if (!hash || hash === "#") return; // 空のhrefは対象外、ブラウザ標準の挙動に任せる

      const targetEl = document.getElementById(hash.slice(1));
      if (!targetEl) return; // 対象がページ内に無ければブラウザ標準の挙動に任せる

      event.preventDefault();
      animateScrollTo(targetEl.getBoundingClientRect().top + window.scrollY);
    });
  })();

  /* ==========================================================================
     スクロール連動の固定メニュー (#js-fixed--menu)
     div#js-fixed--menuの位置までスクロールしたらbody.is-fixedを付与し、
     position:fixedに切り替える(CSS側はbody.is-fixed #js-fixed--menuで指定)。
     一度固定されたら、その後menuの元の位置より上に戻るまでbody.is-fixedは
     つけたままにする。
     また、.js-tab--content-groupの下端の手前400pxにスクロールが達したら
     フェードアウトで非表示にし、400px以上離れたら再度フェードインで表示する。
     ページ内の他の機能とは独立したIIFE。
     ========================================================================== */
  (() => {
    const menu = document.getElementById("js-fixed--menu");
    const contentGroup = document.querySelector(".js-tab--content-group");
    const fixedContent = document.querySelector(".js-fixed--content");
    const spHeaderLogo = document.querySelector(".header-mh-sp__logo");
    if (!menu || !contentGroup) return;

    const HIDE_OFFSET_PX = 400; // .js-tab--content-groupの下端からこの距離まで来たら非表示にする
    const SP_HEADER_BREAKPOINT_PX = 900; // .header-mh-spがdisplay:blockになるCSSのブレイクポイントと合わせる

    // is-fixedが付いていない(通常フローに乗っている)間のmenuの上端位置(ドキュメント座標)・
    // 幅・高さ。position:fixedになるとwidthがauto(shrink-to-fit)になり、
    // それにつられて高さも変わってしまうため、通常フロー時の値をキャッシュして使い回す。
    let menuTop = menu.getBoundingClientRect().top + window.scrollY;
    let menuWidth = menu.offsetWidth;
    let menuHeight = menu.offsetHeight;

    const updateFixedMenu = () => {
      // is-fixedになるまでの間は、scrollのたびに毎回menuTop/menuWidth/menuHeightを
      // 測り直す。#js-fixed--menuより上にある画像・動画(サイズ未指定のためload後に
      // サイズが確定する)やサードパーティスクリプトの遅延挿入などでload/resize後にも
      // レイアウトが変わるページがあり、その場合に古い位置のまま判定してしまうと
      // is-fixedが付与されるタイミングが実際の表示とずれてしまうため。
      // (is-fixed中はposition:fixedで正しい元の値が測れないため測り直さない)
      if (!document.body.classList.contains("is-fixed")) {
        menuTop = menu.getBoundingClientRect().top + window.scrollY;
        menuWidth = menu.offsetWidth;
        menuHeight = menu.offsetHeight;
      }

      const scrollTop = window.scrollY;

      // window幅900px以下ではdiv.header-mh-sp(内のheader-mh-sp__logo)がdisplay:blockの
      // 固定ヘッダーとして常に上に重なるため、その高さ分だけ手前(上)でis-fixedに
      // 切り替える。900pxより広い場合はヘッダー分の補正は不要。
      const isMobileHeader = window.innerWidth <= SP_HEADER_BREAKPOINT_PX;
      const spHeaderHeight = isMobileHeader && spHeaderLogo ? spHeaderLogo.offsetHeight : 0;
      const fixTriggerTop = menuTop - spHeaderHeight;

      // スクロール位置がmenuの元の位置(モバイルヘッダー補正込み)より下に来たら
      // position:fixedに切り替え、上に戻ったら解除する
      const isFixed = scrollTop >= fixTriggerTop;
      document.body.classList.toggle("is-fixed", isFixed);

      // position:fixed時のみ、モバイルヘッダー分だけmenuの上端をずらして重ならないようにする
      menu.style.top = isFixed ? (isMobileHeader ? `${spHeaderHeight}px` : "0") : "";

      // position:fixedでwidthがshrink-to-fitになり高さが変わってしまわないよう、
      // 通常フロー時の幅を明示的に指定しておく
      menu.style.width = isFixed ? `${menuWidth}px` : "";

      // is-fixedでmenuがフローから外れる分、js-fixed--contentにmenuの高さ分の
      // padding-topを入れて後続コンテンツが詰め上がらないようにする
      if (fixedContent) {
        fixedContent.style.paddingTop = isFixed ? `${menuHeight}px` : "0";
      }

      // .js-tab--content-groupの下端の手前400pxに達したらフェードアウトする
      const hideFrom = contentGroup.offsetTop + contentGroup.offsetHeight - HIDE_OFFSET_PX;
      menu.classList.toggle("is-hidden", scrollTop >= hideFrom);
    };

    window.addEventListener("scroll", updateFixedMenu, { passive: true });
    window.addEventListener("resize", updateFixedMenu);
    window.addEventListener("load", updateFixedMenu);
    updateFixedMenu(); // 初期表示時点で既に条件を満たしていれば即時反映する

    // タブ切り替え時のスクロール(#js-tab--triggerクリック時など)が、
    // 固定メニューの高さ分だけ手前で止まれるように現在のmenuHeightを公開しておく
    window.jsFixedMenu = { getHeight: () => menuHeight };
  })();

async function renderCartSummary() {
  const res = await fetch('/cart.js');
  const cart = await res.json();
  const cartTest = document.getElementById("cart-test");

  console.log(cart.items);

  if (cart.items.length){
    cartTest.innerHTML = "<p><strong>買い物かごの内容</strong></p>";
    cart.items.forEach(item => {
      cartTest.insertAdjacentHTML('beforeend', '<p>' + item.product_title + '<br>注文数：' + item.quantity + '</p>');
      if (item.properties) {
        Object.entries(item.properties).forEach(([key, value]) => {
          if (value) {
            cartTest.insertAdjacentHTML('beforeend', '<p>' + key + ':' + value + '</p>');
          }
        });
      }
    });
  } else {
      cartTest.innerHTML = "<p><strong>買い物かごには何も入っていません。</strong></p>";
  }
}